import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import App from './App';

// Lazy load the pay calendar page for better performance
const PayCalendarPage = React.lazy(() => import('./pages/PayCalendarPage'));
const MentionsLegalesPage = React.lazy(() => import('./pages/MentionsLegalesPage'));

/**
 * @fileoverview Main routing component for the salary simulator
 * 
 * This component handles all routing logic for the application with support for:
 * - Default route (/) for the base IDE profession
 * - Dynamic routes for nursing specializations (/:professionId)
 * - Territory-specific routes (/:territoryId or /:professionId/:territoryId)
 * - Grade and echelon specific routes with all combinations
 * - Fallback redirect for invalid routes
 * 
 * The App component is rendered by each route and uses useParams
 * to determine which profession, territory, grade, and echelon to display.
 * 
 * Route priority (most specific first):
 * 1. Profession + Territory + Grade + Echelon
 * 2. Profession + Grade + Echelon (France métropolitaine)
 * 3. Territory + Grade + Echelon (default IDE profession)
 * 4. Grade + Echelon (default IDE profession, France métropolitaine)
 * 5. Profession + Territory
 * 6. Profession or Territory alone
 * 7. Default route
 * 
 * @author App'Ines Team
 * @version 2.0.0
 * @since 2025
 */

export default function AppRouter() {
  return (
    <Routes>
      {/* Routes statiques (prioritaires pour éviter conflit avec /:professionId) */}
      <Route path="/mentions-legales" element={
        <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 animate-spin rounded-full border-2 border-appines-main border-t-transparent mx-auto mb-2"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        </div>}>
          <MentionsLegalesPage />
        </React.Suspense>
      } />
      <Route path="/paie-fonctionnaire" element={
        <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 animate-spin rounded-full border-2 border-appines-main border-t-transparent mx-auto mb-2"></div>
            <p className="text-gray-600">Chargement du calendrier...</p>
          </div>
        </div>}>
          <PayCalendarPage />
        </React.Suspense>
      } />

      {/* Routes avec grade et échelon - ORDRE CRITIQUE : du plus spécifique au plus général */}

      {/* 1. Profession + Territoire + Grade + Échelon */}
      <Route path="/:professionId/:territoryId/:gradeId/:echelonId" element={<App />} />
      
      {/* 2. Profession + Grade + Échelon (France métropolitaine) */}
      <Route path="/:professionId/:gradeId/:echelonId" element={<App />} />
      
      {/* 3. Territoire + Grade + Échelon (profession IDE par défaut) */}
      <Route path="/:territoryId/:gradeId/:echelonId" element={<App />} />
      
      {/* 4. Grade + Échelon seuls (profession IDE par défaut, France métropolitaine) */}
      <Route path="/:gradeId/:echelonId" element={<App />} />
      
      {/* Routes existantes - PRÉSERVÉES pour compatibilité */}
      
      {/* 5. Profession + Territoire (sans grade/échelon) */}
      <Route path="/:professionId/:territoryId" element={<App />} />
      
      {/* 6. Profession seule OU Territoire seul (sans grade/échelon) */}
      <Route path="/:professionId" element={<App />} />
      
      {/* 7. Route par défaut pour IDE en France métropolitaine */}
      <Route path="/" element={<App />} />

      {/* Redirection pour toute autre route vers la profession par défaut */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}